import { createContext, useContext, useMemo, useState, ReactNode, useCallback, useEffect } from 'react';
import { setAccessToken } from '../services/api';
import { authApi } from '../services/auth';
import type { AuthUser } from '../types/api';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  /**
   * 手机号强制验证总开关的运行时快照（来源 /auth/policy-version）。
   * 未加载完 / 拉取失败时保持 false —— 安全默认（回退到旧的邮箱密码流程，不弹手机 gate）。
   */
  phoneVerifyEnabled: boolean;
  login: (user: AuthUser, accessToken: string) => void;
  updateUser: (user: AuthUser) => void;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  registerWithCredentials: (input: { email: string; password: string; realName?: string; institution?: string }) => Promise<void>;
  registerWithPhone: (input: { phone: string; code: string; password?: string; email?: string; acceptPrivacy: boolean; privacyVersion: number }) => Promise<void>;
  loginWithPhone: (input: { phone: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [phoneVerifyEnabled, setPhoneVerifyEnabled] = useState(false);

  const login = useCallback((nextUser: AuthUser, accessToken: string) => {
    setAccessToken(accessToken);
    setUser(nextUser);
  }, []);

  // 就地更新当前登录用户（无新 token 的场景，如绑定手机后 bindPhone 只回 { user }）。
  const updateUser = useCallback((nextUser: AuthUser) => {
    setUser(nextUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const loginWithCredentials = useCallback(
    async (email: string, password: string) => {
      const res = await authApi.login({ email, password });
      login(res.data.user, res.data.accessToken);
    },
    [login],
  );

  const registerWithCredentials = useCallback(
    async (input: { email: string; password: string; realName?: string; institution?: string }) => {
      const res = await authApi.register(input);
      login(res.data.user, res.data.accessToken);
    },
    [login],
  );

  const registerWithPhone = useCallback(
    async (input: { phone: string; code: string; password?: string; email?: string; acceptPrivacy: boolean; privacyVersion: number }) => {
      const res = await authApi.registerPhone(input);
      login(res.data.user, res.data.accessToken);
    },
    [login],
  );

  const loginWithPhone = useCallback(
    async (input: { phone: string; code: string }) => {
      const res = await authApi.loginPhone(input);
      login(res.data.user, res.data.accessToken);
    },
    [login],
  );

  const refresh = useCallback(async () => {
    try {
      const res = await authApi.refresh();
      login(res.data.user, res.data.accessToken);
    } catch {
      setUser(null);
      setAccessToken(null);
    }
  }, [login]);

  // 启动时读取「手机号强制验证」总开关；失败保持 false（安全默认）。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.policyVersion();
        if (!cancelled) setPhoneVerifyEnabled(res.data.phoneVerifyEnabled === true);
      } catch {
        if (!cancelled) setPhoneVerifyEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.refresh();
        if (!cancelled) {
          login(res.data.user, res.data.accessToken);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [login]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      phoneVerifyEnabled,
      login,
      updateUser,
      loginWithCredentials,
      registerWithCredentials,
      registerWithPhone,
      loginWithPhone,
      logout,
      refresh,
    }),
    [user, loading, phoneVerifyEnabled, login, updateUser, loginWithCredentials, registerWithCredentials, registerWithPhone, loginWithPhone, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
